import { Routes } from "@angular/router";
import { loadTranslationResolver } from "@app/kernel/translations/resolvers/load-translations.resolver";
import { COMMON_GUARD_OPTIONS } from "@app/shared/auth/constants/permissions.constant";
import { AuthGuard } from "@app/shared/auth/guards/auth.guard";
import { TRANSLATION_PATHS } from "@app/shared/general/translation/constants/translation.constant";
import { AffiliateApiService } from "./domains/affiliates/services/affiliate-api.service";
import { AreaApiService } from "./domains/areas/services/area-api.service";
import { MicroAreaApiService } from "./domains/micro-areas/services/micro-area-api.service";
import { CountryApiService } from "./domains/countries/services/country-api.service";
import { LocationPlacementApiService } from "./domains/location-placements/services/location-placement-api.service";
import { LocationApiService } from "../../shared/company-hierarchy/locations/services/location-api.service";
import { LandApiService } from "./domains/lands/services/land-api.service";
import { DeskApiService } from "./domains/desks/services/desk-api.service";

export const companyHierarchyRoutes: Routes = [
  {
    path: "directories",
    resolve: {
      translation: loadTranslationResolver([
        TRANSLATION_PATHS.ROOT,
        TRANSLATION_PATHS.COMPANY_HIERARCHY.SHARED,
        TRANSLATION_PATHS.COMPANY_HIERARCHY.DIRECTORIES,
        TRANSLATION_PATHS.SETTINGS.SYSTEM_SETTINGS,
      ]),
    },
    loadChildren: () => import("./domains/directories/directories.routes").then(r => r.directoriesRoutes),
    canActivate: [AuthGuard],
    data: { authGuardOptions: COMMON_GUARD_OPTIONS.DIRECTORY.VIEW },
  },
  {
    path: "countries",
    resolve: {
      translation: loadTranslationResolver([
        TRANSLATION_PATHS.ROOT,
        TRANSLATION_PATHS.COMPANY_HIERARCHY.SHARED,
        TRANSLATION_PATHS.COMPANY_HIERARCHY.COUNTRIES,
      ]),
    },
    loadChildren: () => import("./domains/countries/countries.routes").then(r => r.countriesRoutes),
    canActivate: [AuthGuard],
    data: { authGuardOptions: COMMON_GUARD_OPTIONS.COUNTRY.VIEW },
    providers: [CountryApiService],
  },
  {
    path: "lands",
    resolve: {
      translation: loadTranslationResolver([
        TRANSLATION_PATHS.ROOT,
        TRANSLATION_PATHS.COMPANY_HIERARCHY.SHARED,
        TRANSLATION_PATHS.COMPANY_HIERARCHY.LANDS,
        TRANSLATION_PATHS.COMPANY_HIERARCHY.LOCATIONS,
      ]),
    },
    loadChildren: () => import("./domains/lands/lands.routes").then(r => r.landsRoutes),
    canActivate: [AuthGuard],
    data: { authGuardOptions: COMMON_GUARD_OPTIONS.LAND.VIEW },
    providers: [LandApiService],
  },
  {
    path: "areas",
    resolve: {
      translation: loadTranslationResolver([
        TRANSLATION_PATHS.ROOT,
        TRANSLATION_PATHS.COMPANY_HIERARCHY.SHARED,
        TRANSLATION_PATHS.COMPANY_HIERARCHY.AREAS,
        TRANSLATION_PATHS.COMPANY_HIERARCHY.LOCATIONS,
      ]),
    },
    loadChildren: () => import("./domains/areas/areas.routes").then(r => r.areasRoutes),
    canActivate: [AuthGuard],
    data: { authGuardOptions: COMMON_GUARD_OPTIONS.AREA.VIEW },
    providers: [AreaApiService],
  },
  {
    path: "micro-areas",
    resolve: {
      translation: loadTranslationResolver([
        TRANSLATION_PATHS.ROOT,
        TRANSLATION_PATHS.COMPANY_HIERARCHY.SHARED,
        TRANSLATION_PATHS.COMPANY_HIERARCHY.LOCATIONS,
        TRANSLATION_PATHS.COMPANY_HIERARCHY.MICRO_AREAS,
        TRANSLATION_PATHS.COMPANY_HIERARCHY.LOCATIONS,
        TRANSLATION_PATHS.USERS.SHARED,
      ]),
    },
    loadChildren: () => import("./domains/micro-areas/micro-areas.routes").then(r => r.microAreasRoutes),
    canActivate: [AuthGuard],
    data: { authGuardOptions: COMMON_GUARD_OPTIONS.MICRO_AREA.VIEW },
    providers: [MicroAreaApiService],
  },
  {
    path: "affiliates",
    resolve: {
      translation: loadTranslationResolver([
        TRANSLATION_PATHS.ROOT,
        TRANSLATION_PATHS.COMPANY_HIERARCHY.SHARED,
        TRANSLATION_PATHS.COMPANY_HIERARCHY.AFFILIATES,
        TRANSLATION_PATHS.COMPANY_HIERARCHY.LOCATIONS,
      ]),
    },
    loadChildren: () => import("./domains/affiliates/affiliates.routes").then(r => r.affiliatesRoutes),
    canActivate: [AuthGuard],
    data: { authGuardOptions: COMMON_GUARD_OPTIONS.AFFILIATE.VIEW },
    providers: [AffiliateApiService],
  },
  {
    path: "location-placements",
    resolve: {
      translation: loadTranslationResolver([
        TRANSLATION_PATHS.ROOT,
        TRANSLATION_PATHS.COMPANY_HIERARCHY.SHARED,
        TRANSLATION_PATHS.COMPANY_HIERARCHY.LOCATION_PLACEMENTS,
      ]),
    },
    loadChildren: () => import("./domains/location-placements/location-placements.routes").then(r => r.locationPlacementsRoutes),
    canActivate: [AuthGuard],
    data: { authGuardOptions: COMMON_GUARD_OPTIONS.LOCATION_PLACEMENT.VIEW },
    providers: [LocationPlacementApiService],
  },
  {
    path: "locations",
    resolve: {
      translation: loadTranslationResolver([
        TRANSLATION_PATHS.ROOT,
        TRANSLATION_PATHS.COMPANY_HIERARCHY.SHARED,
        TRANSLATION_PATHS.COMPANY_HIERARCHY.LOCATIONS,
        TRANSLATION_PATHS.COMPANY_HIERARCHY.LOCATION_PLACEMENTS,
        TRANSLATION_PATHS.COMPANY_HIERARCHY.AFFILIATES,
        TRANSLATION_PATHS.COMPANY_HIERARCHY.OFFICES,
        TRANSLATION_PATHS.COMPANY_HIERARCHY.OFFICE_RULE,
      ]),
    },
    loadChildren: () => import("./domains/locations/locations.routes").then(r => r.locationsRoutes),
    canActivate: [AuthGuard],
    data: { authGuardOptions: COMMON_GUARD_OPTIONS.LOCATION.VIEW },
    providers: [LocationApiService],
  },
  {
    path: "desks",
    resolve: {
      translation: loadTranslationResolver([
        TRANSLATION_PATHS.ROOT,
        TRANSLATION_PATHS.COMPANY_HIERARCHY.SHARED,
        TRANSLATION_PATHS.COMPANY_HIERARCHY.DESKS,
        TRANSLATION_PATHS.COMPANY_HIERARCHY.LOCATIONS,
      ]),
    },
    loadChildren: () => import("./domains/desks/desks.routes").then(r => r.desksRoutes),
    canActivate: [AuthGuard],
    data: { authGuardOptions: COMMON_GUARD_OPTIONS.DESK.VIEW },
    providers: [DeskApiService],
  },
];
